'use client'

import { useRef, useEffect, useCallback, useState } from 'react'
import type { ScrollMethods } from './types'

/**
 * Custom hook that returns scroll methods for a scrollable container or window.
 * If no container ID is provided, it defaults to the window's scroll methods.
 *
 * @function useScrollMethods
 * @param {string} [containerId] - The ID of the scrollable container. If not provided, window methods are used.
 * @returns {object} An object containing a reference to the scroll methods for the container or window, and the element itself.
 * @throws Will throw an error if the provided ID is invalid or if the element is not an HTMLDivElement.
 *
 * @example
 * // Use the hook for window scroll methods
 * const { scrollMethodsRef, element } = useScrollMethods();
 *
 * // Use the hook for a specific scrollable container
 * const { scrollMethodsRef, element } = useScrollMethods('container-id');
 */
export function useScrollMethods(): {
  scrollMethodsRef: React.MutableRefObject<ScrollMethods | null>
  scrollContainer: Window | null
}
export function useScrollMethods(containerId?: string): {
  scrollMethodsRef: React.MutableRefObject<ScrollMethods | null>
  scrollContainer: HTMLDivElement | null
}
export function useScrollMethods(containerId?: string): {
  scrollMethodsRef: React.MutableRefObject<ScrollMethods | null>
  scrollContainer: Window | HTMLDivElement | null
} {
  const scrollMethodsRef = useRef<ScrollMethods | null>(null)
  const [scrollContainer, setScrollContainer] = useState<
    Window | HTMLDivElement | null
  >(null)

  const getScrollMethods = useCallback(
    (element: Window | HTMLDivElement): ScrollMethods => {
      const isWindow = element === window
      return {
        scrollTo: ({
          x,
          y,
          behavior = 'auto',
        }: {
          x?: number
          y?: number
          behavior?: ScrollBehavior
        }) => {
          element.scrollTo({
            top:
              y !== undefined
                ? y
                : isWindow
                ? window.scrollY
                : (element as HTMLDivElement).scrollTop,
            left:
              x !== undefined
                ? x
                : isWindow
                ? window.scrollX
                : (element as HTMLDivElement).scrollLeft,
            behavior,
          })
        },
        scrollBy: ({
          dx,
          dy,
          behavior = 'auto',
        }: {
          dx?: number
          dy?: number
          behavior?: ScrollBehavior
        }) => {
          element.scrollBy({
            top: dy !== undefined ? dy : 0,
            left: dx !== undefined ? dx : 0,
            behavior,
          })
        },
        getScrollPosition: () => ({
          x: isWindow ? window.scrollX : (element as HTMLDivElement).scrollLeft,
          y: isWindow ? window.scrollY : (element as HTMLDivElement).scrollTop,
        }),
        getScrollHeight: () =>
          isWindow
            ? document.documentElement.scrollHeight
            : (element as HTMLDivElement).scrollHeight,
        getVisibleHeight: () =>
          isWindow
            ? window.innerHeight
            : (element as HTMLDivElement).clientHeight,
      }
    },
    [],
  )

  const setupScrollMethods = useCallback(() => {
    let targetElement: Window | HTMLDivElement = window

    if (containerId) {
      const container = document.getElementById(containerId)

      if (!container) {
        console.error(`Error: No container found with ID: '${containerId}'`)
        scrollMethodsRef.current = getScrollMethods(window)
        setScrollContainer(window)
        return
      }

      if (!(container instanceof HTMLDivElement)) {
        console.error(
          `Error: Element with ID '${containerId}' is not an HTMLDivElement`,
        )
        scrollMethodsRef.current = getScrollMethods(window)
        setScrollContainer(window)
        return
      }

      targetElement = container
    }

    scrollMethodsRef.current = getScrollMethods(targetElement)
    setScrollContainer(targetElement)
  }, [containerId, getScrollMethods])

  // Initialize scroll methods when the component mounts or containerId changes
  useEffect(() => {
    setupScrollMethods()
  }, [setupScrollMethods])

  return { scrollMethodsRef, scrollContainer }
}
